// ============================================
// pages/Salon/SalonServices.js
// Salon Services Management — Full CRUD
// ============================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SALON_API_URL } from '../../api/config';
import {
  Plus, X, Edit2, Trash2, Search, Scissors, Filter,
  Clock, User, Users, UserCheck,
  Loader2, MoreVertical, RefreshCw, Sparkles, ChevronDown,
  Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Download,
} from 'lucide-react';

// ─── Taxonomy ─────────────────────────────────────────────────────────────────

const GENDER_OPTIONS = ['Men', 'Women', 'Unisex'];

const CATEGORIES = {
  Men:    ['Mens'],
  Women:  ['Skin', 'Hair', 'Waxing', 'Nails', 'Massage', 'Makeup'],
  Unisex: ['Skin', 'Hair', 'Massage', 'Other'],
};

const SUBCATEGORIES = {
  Men: {
    Mens: ['Haircut', 'Shave', 'Beard Trim', 'Facials', 'Head Massage', 'Hair Colour', 'Other'],
  },
  Women: {
    Skin:    ['Clean-Up', 'D-Tan', 'Bleach', 'Facials'],
    Hair:    ['Hair Cutting and Styling', 'Colouring', 'Hair Spa and Treatment'],
    Waxing:  ['Honey', 'Chocolate', 'Rica', 'Brazilian', 'Other'],
    Nails:   ['Hand/Foot', 'Manicure', 'Pedicure'],
    Massage: ['Massage'],
    Makeup:  ['Basic Makeup', 'Advanced Makeup', 'Pre-Bridal Premium', 'Pre-Bridal Ultra Premium', 'Bridal Makeup', 'Other'],
  },
  Unisex: {
    Skin:    [],
    Hair:    [],
    Massage: [],
    Other:   [],
  },
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

const GENDER_ICONS  = { Men: User, Women: UserCheck, Unisex: Users };
const GENDER_COLORS = {
  Men:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Women:  'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  Unisex: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};
const CAT_COLORS = [
  'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '', gender: '', category: '', subcategory: '',
  hasTypes: false, types: [{ name: '', price: '' }],
  price: '', duration: 30, description: '',
};

function formatDuration(mins) {
  if (!mins) return '30 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function formatPrice(service) {
  if (service.types && service.types.length > 0) {
    const prices = service.types.map(t => Number(t.price)).filter(Boolean);
    if (!prices.length) return '—';
    const mn = Math.min(...prices), mx = Math.max(...prices);
    return mn === mx ? `₹${mn}` : `₹${mn} – ₹${mx}`;
  }
  return service.price ? `₹${service.price}` : '—';
}

// ─── Service Card ─────────────────────────────────────────────────────────────

function ServiceCard({ service, onEdit, onDelete, onToggle }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const GenderIcon = GENDER_ICONS[service.gender] || User;
  const catIdx = (service.category?.length || 0) % CAT_COLORS.length;

  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-200 p-5 flex flex-col gap-3 relative ${!service.isActive ? 'opacity-60' : ''}`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 dark:text-white text-base leading-tight truncate">
            {service.name}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">{service.subcategory}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!service.isActive && (
            <span className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full font-medium">
              Inactive
            </span>
          )}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <MoreVertical size={15} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-8 z-20 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-100 dark:border-slate-700 py-1 w-40">
                  <button onClick={() => { onEdit(service); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                    <Edit2 size={13} /> Edit
                  </button>
                  <button onClick={() => { onToggle(service); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                    <RefreshCw size={13} /> {service.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>
                  <button onClick={() => { onDelete(service); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5">
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${GENDER_COLORS[service.gender]}`}>
          <GenderIcon size={10} /> {service.gender}
        </span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CAT_COLORS[catIdx]}`}>
          {service.category}
        </span>
      </div>

      {/* Type variants */}
      {service.types?.length > 0 && (
        <div className="flex flex-col gap-1 bg-gray-50 dark:bg-slate-900/50 rounded-xl p-3">
          {service.types.map((t, i) => (
            <div key={i} className="flex justify-between items-center text-xs">
              <span className="text-gray-500 dark:text-gray-400">{t.name}</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200">₹{t.price}</span>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50 dark:border-slate-700">
        <div className="flex items-center gap-1 text-gray-400 text-xs">
          <Clock size={11} />
          {formatDuration(service.duration)}
        </div>
        <span className="font-black text-purple-600 dark:text-purple-400 text-base">
          {formatPrice(service)}
        </span>
      </div>
    </div>
  );
}

// ─── Add/Edit Modal ───────────────────────────────────────────────────────────

function ServiceModal({ mode, service, onClose, onSave }) {
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});

  useEffect(() => {
    if (mode === 'edit' && service) {
      const hasTypes = !!(service.types && service.types.length > 0);
      setForm({
        name:        service.name        || '',
        gender:      service.gender      || '',
        category:    service.category    || '',
        subcategory: service.subcategory || '',
        hasTypes,
        types:  hasTypes ? service.types.map(t => ({ name: t.name, price: String(t.price) })) : [{ name: '', price: '' }],
        price:       hasTypes ? '' : String(service.price || ''),
        duration:    service.duration || 30,
        description: service.description || '',
      });
    }
  }, [mode, service]);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: '' }));
  };
  const handleGenderChange = g => {
    setForm(f => ({ ...f, gender: g, category: '', subcategory: '' }));
    setErrors({});
  };
  const handleCategoryChange = c => {
    setForm(f => ({ ...f, category: c, subcategory: '' }));
    setErrors(e => ({ ...e, category: '', subcategory: '' }));
  };
  const addType    = () => setForm(f => ({ ...f, types: [...f.types, { name: '', price: '' }] }));
  const removeType = i  => setForm(f => ({ ...f, types: f.types.filter((_, idx) => idx !== i) }));
  const setType    = (i, key, val) =>
    setForm(f => ({ ...f, types: f.types.map((t, idx) => idx === i ? { ...t, [key]: val } : t) }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Service name is required';
    if (!form.gender)      e.gender = 'Please select a gender';
    if (!form.category)    e.category = 'Please select a category';
    const subs = SUBCATEGORIES[form.gender]?.[form.category] || [];
    if (subs.length > 0 && !form.subcategory) e.subcategory = 'Please select a subcategory';
    if (form.hasTypes) {
      form.types.forEach((t, i) => {
        if (!t.name.trim())                              e[`tn_${i}`] = 'Name required';
        if (!t.price || isNaN(t.price) || Number(t.price) <= 0) e[`tp_${i}`] = 'Valid price required';
      });
    } else {
      if (!form.price || isNaN(form.price) || Number(form.price) <= 0) e.price = 'Valid price required';
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const subs = SUBCATEGORIES[form.gender]?.[form.category] || [];
      const payload = {
        name:        form.name.trim(),
        gender:      form.gender,
        category:    form.category,
        subcategory: subs.length > 0 ? form.subcategory : form.category,
        duration:    form.duration,
        description: form.description.trim(),
        ...(form.hasTypes
          ? { types: form.types.map(t => ({ name: t.name.trim(), price: Number(t.price) })), price: Math.min(...form.types.map(t => Number(t.price))) }
          : { price: Number(form.price), types: [] }
        ),
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const categories    = CATEGORIES[form.gender] || [];
  const subcategories = SUBCATEGORIES[form.gender]?.[form.category] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
          <div>
            <h2 className="text-lg font-black text-gray-900 dark:text-white">
              {mode === 'add' ? '✨ Add New Service' : '✏️ Edit Service'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the service details below</p>
          </div>
          <button onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Service Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Service Name <span className="text-red-500">*</span>
            </label>
            <input
              className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${errors.name ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
              placeholder="e.g. Deep Cleansing Facial"
              value={form.name}
              onChange={e => set('name', e.target.value)}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Gender */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
              Gender <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {GENDER_OPTIONS.map(g => {
                const Icon   = GENDER_ICONS[g];
                const active = form.gender === g;
                const color  = g === 'Men' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                             : g === 'Women' ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400'
                             : 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
                return (
                  <button key={g} onClick={() => handleGenderChange(g)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-xs font-bold transition-all ${active ? color : 'border-gray-200 dark:border-slate-700 text-gray-500 hover:border-gray-300 dark:hover:border-slate-600'}`}
                  >
                    <Icon size={18} /> {g}
                  </button>
                );
              })}
            </div>
            {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
          </div>

          {/* Category */}
          {form.gender && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button key={cat} onClick={() => handleCategoryChange(cat)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                      form.category === cat
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600'
                    }`}
                  >{cat}</button>
                ))}
              </div>
              {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
            </div>
          )}

          {/* Subcategory */}
          {form.category && subcategories.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Subcategory <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {subcategories.map(sub => (
                  <button key={sub} onClick={() => set('subcategory', sub)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      form.subcategory === sub
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-400'
                        : 'bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:border-purple-200'
                    }`}
                  >{sub}</button>
                ))}
              </div>
              {errors.subcategory && <p className="text-red-500 text-xs mt-1">{errors.subcategory}</p>}
            </div>
          )}

          {/* Types toggle */}
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                Types / Variants <span className="font-normal normal-case text-gray-400">(optional — e.g. Full Arm, Half Arm)</span>
              </p>
            </div>
            <button
              onClick={() => set('hasTypes', !form.hasTypes)}
              className={`relative w-10 h-5 rounded-full transition-colors ${form.hasTypes ? 'bg-purple-600' : 'bg-gray-300 dark:bg-slate-600'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.hasTypes ? 'translate-x-5' : ''}`} />
            </button>
          </div>

          {/* Types list */}
          {form.hasTypes ? (
            <div className="space-y-2.5">
              <p className="text-xs text-gray-500 dark:text-gray-400">Enter each variant with its own price</p>
              {form.types.map((t, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input
                      className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors[`tn_${i}`] ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                      placeholder="Type name (e.g. Full Arm)"
                      value={t.name}
                      onChange={e => setType(i, 'name', e.target.value)}
                    />
                    {errors[`tn_${i}`] && <p className="text-red-500 text-xs mt-0.5">{errors[`tn_${i}`]}</p>}
                  </div>
                  <div className="w-28">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      <input
                        type="number" min="0"
                        className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-6 pr-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors[`tp_${i}`] ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                        placeholder="Price"
                        value={t.price}
                        onChange={e => setType(i, 'price', e.target.value)}
                      />
                    </div>
                    {errors[`tp_${i}`] && <p className="text-red-500 text-xs mt-0.5">{errors[`tp_${i}`]}</p>}
                  </div>
                  {form.types.length > 1 && (
                    <button onClick={() => removeType(i)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors mt-0.5">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addType} className="text-xs text-purple-600 dark:text-purple-400 font-semibold flex items-center gap-1 hover:underline">
                <Plus size={12} /> Add another type
              </button>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">₹</span>
                <input
                  type="number" min="0"
                  className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-8 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.price ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                  placeholder="Enter price"
                  value={form.price}
                  onChange={e => set('price', e.target.value)}
                />
              </div>
              {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
            </div>
          )}

          {/* Duration */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
              Duration <span className="font-normal normal-case text-gray-400">(default 30 min)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {DURATION_OPTIONS.map(d => (
                <button key={d} onClick={() => set('duration', d)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                    form.duration === d
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:border-purple-300'
                  }`}
                >{formatDuration(d)}</button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Description <span className="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={3}
              className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              placeholder="Describe this service..."
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-slate-800 flex gap-3 flex-shrink-0">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {saving ? 'Saving...' : mode === 'add' ? 'Add Service' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Upload Modal ─────────────────────────────────────────────────────

const CSV_TEMPLATE = `name,gender,category,subcategory,price,duration,description,types
Hair Cut,Men,Mens,Haircut,300,30,Classic hair cut,
Hair Spa,Women,Hair,Hair Spa and Treatment,800,60,Deep conditioning hair spa,
Facials,Women,Skin,Facials,600,45,Brightening facial,
"Honey Wax (Full Legs)",Women,Waxing,Honey,500,30,Honey wax for full legs,
"Combo Cut",Men,Mens,Haircut,,,Cut with multiple lengths,"[{\"name\":\"Short\",\"price\":250},{\"name\":\"Long\",\"price\":350}]"
`;

function BulkUploadModal({ onClose, onSuccess, token }) {
  const fileRef   = useRef(null);
  const [file,    setFile]    = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result,  setResult]  = useState(null); // { inserted, skipped, errors }
  const [tab,     setTab]     = useState('upload'); // 'upload' | 'format'

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setResult(null); }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res  = await fetch(`${SALON_API_URL}/services/bulk-upload`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    fd,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Upload failed');
      setResult(data);
      onSuccess();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'services_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <FileSpreadsheet size={18} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 dark:text-white text-base">Bulk Upload Services</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Import services from CSV or Excel</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-slate-700">
          {['upload', 'format'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-2.5 text-sm font-semibold capitalize transition-colors ${
                tab === t
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t === 'upload' ? '📤 Upload File' : '📋 File Format'}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-5">

          {tab === 'upload' && (
            <div className="flex flex-col gap-4">
              {/* Drop zone */}
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-purple-300 dark:border-purple-700 rounded-2xl p-8 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all"
              >
                <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFilePick} />
                <Upload size={32} className="text-purple-400 mx-auto mb-3" />
                {file ? (
                  <div>
                    <p className="font-bold text-gray-800 dark:text-white text-sm">{file.name}</p>
                    <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB · Click to change</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold text-gray-700 dark:text-gray-300 text-sm">Click to select file</p>
                    <p className="text-xs text-gray-400 mt-1">Supports .csv and .xlsx — max 5 MB</p>
                  </div>
                )}
              </div>

              {/* Download template */}
              <button onClick={downloadTemplate}
                className="flex items-center justify-center gap-2 text-sm text-purple-600 dark:text-purple-400 font-semibold hover:underline"
              >
                <Download size={14} /> Download CSV Template
              </button>

              {/* Result summary */}
              {result && (
                <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-4 flex flex-col gap-3">
                  <div className="flex gap-4">
                    <div className="flex-1 text-center">
                      <p className="text-2xl font-black text-emerald-600">{result.inserted}</p>
                      <p className="text-xs text-gray-500 font-medium">Inserted ✅</p>
                    </div>
                    <div className="flex-1 text-center">
                      <p className="text-2xl font-black text-amber-500">{result.skipped}</p>
                      <p className="text-xs text-gray-500 font-medium">Skipped ⚠️</p>
                    </div>
                  </div>
                  {result.errors?.length > 0 && (
                    <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                      {result.errors.map((e, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <AlertCircle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 dark:text-gray-300">
                            Row {e.row} <span className="font-semibold">{e.name}</span>: {e.reason}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === 'format' && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Your CSV/Excel file must have the following columns. Row 1 must be the header row.</p>
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-800">
                      {['Column', 'Required', 'Values / Notes'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-bold text-gray-700 dark:text-gray-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                    {[
                      ['name',        '✅ Yes', 'Service name, e.g. "Hair Cut"'],
                      ['gender',      '✅ Yes', 'Men | Women | Unisex'],
                      ['category',    '✅ Yes', 'Men→Mens · Women→Skin/Hair/Waxing/Nails/Massage/Makeup · Unisex→Skin/Hair/Massage/Other'],
                      ['subcategory', 'Optional', 'e.g. Haircut, Facials, Honey, Colouring'],
                      ['price',       '✅ (if no types)', 'Number, e.g. 500'],
                      ['duration',    'Optional', '15, 30, 45, 60, 90, or 120 (minutes). Default: 30'],
                      ['description', 'Optional', 'Any text'],
                      ['types',       'Optional', 'JSON: [{"name":"Short","price":200},{"name":"Long","price":300}]'],
                    ].map(([col, req, notes]) => (
                      <tr key={col} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                        <td className="px-3 py-2 font-mono font-bold text-purple-600 dark:text-purple-400">{col}</td>
                        <td className="px-3 py-2 text-gray-500">{req}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3">
                <p className="text-xs font-bold text-amber-700 dark:text-amber-300 mb-1">💡 Example row</p>
                <code className="text-xs text-gray-700 dark:text-gray-300 break-all">
                  Hair Cut,Men,Mens,Haircut,300,30,Classic cut,
                </code>
              </div>
              <button onClick={downloadTemplate}
                className="flex items-center justify-center gap-2 text-sm text-purple-600 dark:text-purple-400 font-semibold hover:underline"
              >
                <Download size={14} /> Download CSV Template
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {tab === 'upload' && (
          <div className="p-5 border-t border-gray-100 dark:border-slate-700 flex gap-3">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800"
            >
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && (
              <button onClick={handleUpload} disabled={!file || uploading}
                className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Uploading...' : 'Upload & Import'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteModal({ service, onClose, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center">
          <Trash2 size={22} className="text-red-500" />
        </div>
        <div>
          <h3 className="font-black text-gray-900 dark:text-white text-base">Delete Service?</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Permanently delete <strong className="text-gray-700 dark:text-gray-300">"{service.name}"</strong>? This cannot be undone.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalonServices() {
  const { userToken: token } = useAuth();

  const [services,       setServices]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [filterGender,   setFilterGender]   = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showInactive,   setShowInactive]   = useState(false);
  const [modal,          setModal]          = useState(null); // {type, service?}
  const [deleting,       setDeleting]       = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const headers = {
    Authorization:  `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept:         'application/json',
  };

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterGender !== 'All')   params.set('gender', filterGender);
      if (filterCategory !== 'All') params.set('category', filterCategory);
      if (!showInactive)            params.set('active', 'true');

      const res  = await fetch(`${SALON_API_URL}/services?${params}`, { headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to fetch');
      setServices(data.data);
    } catch (err) {
      toast.error(err.message || 'Could not load services');
    } finally {
      setLoading(false);
    }
  }, [token, filterGender, filterCategory, showInactive]); // eslint-disable-line

  useEffect(() => { if (token) fetchServices(); }, [fetchServices, token]);

  // ── Create ───────────────────────────────────────────────────────────────

  const handleCreate = async (payload) => {
    const res  = await fetch(`${SALON_API_URL}/services`, { method: 'POST', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to create');
    toast.success('Service added! ✨');
    setModal(null);
    fetchServices();
  };

  // ── Update ───────────────────────────────────────────────────────────────

  const handleUpdate = async (payload) => {
    const sid  = modal.service.id;
    const res  = await fetch(`${SALON_API_URL}/services/${sid}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to update');
    toast.success('Service updated! 🎉');
    setModal(null);
    fetchServices();
  };

  // ── Toggle active ────────────────────────────────────────────────────────

  const handleToggle = async (service) => {
    try {
      const url    = service.isActive
        ? `${SALON_API_URL}/services/${service.id}`
        : `${SALON_API_URL}/services/${service.id}/reactivate`;
      const method = service.isActive ? 'DELETE' : 'PATCH';
      const res    = await fetch(url, { method, headers });
      const data   = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed');
      toast.success(service.isActive ? 'Service deactivated' : 'Service reactivated ✅');
      fetchServices();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  // ── Hard Delete ──────────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const sid  = modal.service.id;
      const res  = await fetch(`${SALON_API_URL}/services/${sid}?permanent=true`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to delete');
      toast.success('Service deleted');
      setModal(null);
      fetchServices();
    } catch (err) {
      toast.error(err.message || 'Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived state ────────────────────────────────────────────────────────

  const filtered = services.filter(s => {
    const q = searchQuery.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.subcategory?.toLowerCase().includes(q) ||
      s.category?.toLowerCase().includes(q)
    );
  });

  const grouped = filtered.reduce((acc, s) => {
    const key = `${s.gender} — ${s.category}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const categoryOptions = filterGender !== 'All'
    ? ['All', ...(CATEGORIES[filterGender] || [])]
    : ['All'];

  const totalActive = services.filter(s => s.isActive).length;
  const menCount    = services.filter(s => s.gender === 'Men'    && s.isActive).length;
  const womenCount  = services.filter(s => s.gender === 'Women'  && s.isActive).length;
  const unisexCount = services.filter(s => s.gender === 'Unisex' && s.isActive).length;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 min-h-full">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <Scissors size={22} className="text-purple-600" />
            Services
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage all salon services · {totalActive} active
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBulkUpload(true)}
            className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 text-purple-600 dark:text-purple-400 px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
          >
            <Upload size={16} /> Bulk Upload
          </button>
          <button
            onClick={() => setModal({ type: 'add' })}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-purple-200 dark:shadow-purple-900/30 transition-all hover:scale-105"
          >
            <Plus size={16} /> Add Service
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Active', value: totalActive, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20' },
          { label: 'Men',          value: menCount,    color: 'text-blue-600 dark:text-blue-400',     bg: 'bg-blue-50 dark:bg-blue-900/20'   },
          { label: 'Women',        value: womenCount,  color: 'text-pink-600 dark:text-pink-400',     bg: 'bg-pink-50 dark:bg-pink-900/20'   },
          { label: 'Unisex',       value: unisexCount, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl p-4 text-center`}>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 mb-6 flex flex-wrap gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Search services..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Gender filter */}
        <div className="flex gap-1.5">
          {['All', ...GENDER_OPTIONS].map(g => (
            <button key={g}
              onClick={() => { setFilterGender(g); setFilterCategory('All'); }}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                filterGender === g
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-600'
              }`}
            >{g}</button>
          ))}
        </div>

        {/* Category filter */}
        {filterGender !== 'All' && categoryOptions.length > 1 && (
          <div className="relative">
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
            >
              {categoryOptions.map(c => <option key={c}>{c}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        )}

        {/* Inactive toggle */}
        <button
          onClick={() => setShowInactive(v => !v)}
          className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
            showInactive
              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
              : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400'
          }`}
        >
          <Filter size={12} /> {showInactive ? 'Showing all' : 'Active only'}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 size={28} className="animate-spin text-purple-600" />
          <p className="text-sm text-gray-400">Loading services...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center">
            <Scissors size={28} className="text-purple-400" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
            {searchQuery ? 'No services match your search' : 'No services yet'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setModal({ type: 'add' })}
              className="text-purple-600 dark:text-purple-400 text-sm font-semibold flex items-center gap-1 hover:underline"
            >
              <Plus size={14} /> Add your first service
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{group}</h2>
                <span className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map(s => (
                  <ServiceCard
                    key={s.id}
                    service={s}
                    onEdit={svc => setModal({ type: 'edit', service: svc })}
                    onDelete={svc => setModal({ type: 'delete', service: svc })}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'add' && (
        <ServiceModal mode="add" onClose={() => setModal(null)} onSave={handleCreate} />
      )}
      {modal?.type === 'edit' && (
        <ServiceModal mode="edit" service={modal.service} onClose={() => setModal(null)} onSave={handleUpdate} />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal service={modal.service} onClose={() => setModal(null)} onConfirm={handleDelete} loading={deleting} />
      )}
      {showBulkUpload && (
        <BulkUploadModal
          token={token}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => { fetchServices(); }}
        />
      )}
    </div>
  );
}